import type { CompanyLocationsListPreviewOutDTO } from '@api/ringil-api.v2';

/**
 * The calendar selection outlives the locations it points at: it is persisted in the URL, in local
 * storage and in saved filters, so a location that is deleted or superseded afterwards stays in
 * `locationIds`/`rampIds`. The filter dropdown is built from the loaded location list and therefore
 * stops offering it, but the reservation request still asked for it.
 *
 * Everything the backend no longer returns for the calendar is dropped here, so a stale id can
 * neither be fetched nor stay in the persisted filter.
 */
export interface CalendarSelection {
  locationIds: string[];
  rampIds: string[];
}

type KnownLocation = Pick<CompanyLocationsListPreviewOutDTO, 'locationId' | 'ramps'>;

export const pruneCalendarSelection = (selection: CalendarSelection, locations: KnownLocation[]): CalendarSelection => {
  const knownLocationIds = new Set(locations.map(l => l.locationId));
  const knownRampIds = new Set(locations.flatMap(l => l.ramps ?? []).map(r => r.rampId));

  return {
    locationIds: (selection.locationIds ?? []).filter(id => knownLocationIds.has(id)),
    rampIds: (selection.rampIds ?? []).filter(id => knownRampIds.has(id)),
  };
};

/** True when pruning would change anything — lets callers skip a pointless filter write / refetch. */
export const hasStaleSelection = (selection: CalendarSelection, locations: KnownLocation[]): boolean => {
  const pruned = pruneCalendarSelection(selection, locations);
  return pruned.locationIds.length !== (selection.locationIds ?? []).length || pruned.rampIds.length !== (selection.rampIds ?? []).length;
};
